// src/hooks/useAdminNotifications.js
// Système de notifications multi-admin — chaque admin a son propre état "vu"
// Structure Firestore :
//   adminSeenState/{uid}           → lastSeen par collection (propre à chaque admin)
//   notifications/{notifId}        → historique partagé (visible par tous les admins)

import { db } from '@/Firebase';
import { getAuth } from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/* ------------------------------------------------------------------ */
/*  Routes avec collections Firestore à surveiller                     */
/* ------------------------------------------------------------------ */
export const WATCHED_ROUTES = [
  { path: '/admin/terrain',     label: 'Achat de lot de terrain',     collection: 'terrainCollection'    },
  { path: '/admin/appartement', label: "Achat d'appartement",         collection: 'appartementCollection' },
  { path: '/admin/maison',      label: 'Achat de maison',             collection: 'maisonCollection'     },
  { path: '/admin/entreprise',  label: "Création d'entreprise",       collection: 'entrepriseCollection' },
  { path: '/admin/banque',      label: 'Demande de crédit bancaire',  collection: 'bankCollection1'      },
  { path: '/admin/bb2',         label: 'Éligibilité crédit bancaire', collection: 'bankCollection2'      },
];

/* ------------------------------------------------------------------ */
/*  Cache EXTERNE — un Set et un flag d'init PAR collection            */
/*  Survit aux remontages/navigations et gère chaque listener séparé  */
/* ------------------------------------------------------------------ */
const knownDocIdsCache       = {}; // { [collectionName]: Set<string> }
const knownDocIdsInitialized = {}; // { [collectionName]: boolean }

// Pré-remplir pour chaque collection surveillée
WATCHED_ROUTES.forEach(r => {
  knownDocIdsCache[r.collection]       = new Set();
  knownDocIdsInitialized[r.collection] = false;
});

/* ------------------------------------------------------------------ */
/*  Son de notification (Web Audio API)                                */
/* ------------------------------------------------------------------ */
function playNotificationSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    // Note principale — 440 Hz, longue résonance
    const g1 = ctx.createGain();
    g1.gain.setValueAtTime(0.5, now);
    g1.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 440;
    osc1.connect(g1);
    g1.connect(ctx.destination);

    // Harmonique légère — 880 Hz, s'estompe vite
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.15, now);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 880;
    osc2.connect(g2);
    g2.connect(ctx.destination);

    osc1.start(now); osc1.stop(now + 0.8);
    osc2.start(now); osc2.stop(now + 0.5);
    osc1.onended = () => { g1.disconnect(); g2.disconnect(); ctx.close(); };
  } catch (e) {
    console.warn('Notification sound failed:', e);
  }
}

/* ------------------------------------------------------------------ */
/*  Hook principal                                                      */
/* ------------------------------------------------------------------ */
export function useAdminNotifications() {
  const auth = getAuth();
  const uid  = auth.currentUser?.uid ?? null;

  const seenDocRef = useMemo(
    () => (uid ? doc(db, 'adminSeenState', uid) : null),
    [uid]
  );

  const [isReady,       setIsReady]       = useState(false);
  const [lastSeen,      setLastSeen]      = useState({});
  const [totalSizes,    setTotalSizes]    = useState({});
  const [badge,         setBadge]         = useState({});
  const [notifications, setNotifications] = useState([]);
  const [soundEnabled,  setSoundEnabled]  = useState(true);

  // Ref miroir de soundEnabled — les closures onSnapshot lisent toujours
  // la valeur à jour sans avoir besoin de recréer les listeners.
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  /* -------------------------------------------------------------- */
  /*  1. Charger lastSeen propre à cet admin                         */
  /* -------------------------------------------------------------- */
  useEffect(() => {
    if (!seenDocRef) return;
    let cancelled = false;

    (async () => {
      try {
        const snap = await getDoc(seenDocRef);
        if (cancelled) return;

        if (snap.exists()) {
          setLastSeen(snap.data() ?? {});
        } else {
          await setDoc(seenDocRef, {});
        }
        setIsReady(true);
      } catch (err) {
        console.error('useAdminNotifications — chargement lastSeen:', err);
        setIsReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, [seenDocRef]);

  /* -------------------------------------------------------------- */
  /*  2. Écouter les collections Firestore en temps réel             */
  /* -------------------------------------------------------------- */
  useEffect(() => {
    if (!isReady) return;

    const unsubs = WATCHED_ROUTES.map(r => {
      const colRef = collection(db, r.collection);

      return onSnapshot(
        colRef,
        snap => {
          setTotalSizes(prev => ({ ...prev, [r.path]: snap.size }));

          // ── Premier snapshot de CETTE collection ──
          // Chaque collection a son propre flag indépendant.
          // Les 6 listeners s'initialisent chacun séparément sans interférer.
          if (!knownDocIdsInitialized[r.collection]) {
            snap.docs.forEach(d => knownDocIdsCache[r.collection].add(d.id));
            knownDocIdsInitialized[r.collection] = true;
            return;
          }

          // ── Snapshots suivants : détecter les vrais nouveaux docs ──
          const cache   = knownDocIdsCache[r.collection];
          const newDocs = snap.docs.filter(d => !cache.has(d.id));

          if (newDocs.length > 0) {
            // Enregistrer les nouveaux IDs
            newDocs.forEach(d => cache.add(d.id));

            // Son uniquement si son activé
            if (soundEnabledRef.current) {
              playNotificationSound();
            }
          }

          // Nettoyer les IDs supprimés du cache
          const currentIds = new Set(snap.docs.map(d => d.id));
          cache.forEach(id => {
            if (!currentIds.has(id)) cache.delete(id);
          });
        },
        err => console.error(`Snapshot error [${r.collection}]:`, err)
      );
    });

    return () => unsubs.forEach(u => u());
  }, [isReady]);

  /* -------------------------------------------------------------- */
  /*  3. Écouter l'historique partagé des notifications              */
  /* -------------------------------------------------------------- */
  useEffect(() => {
    if (!isReady) return;

    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({
        id:        d.id,
        ...d.data(),
        isRead:    (d.data().readBy ?? []).includes(uid),
        createdAt: d.data().createdAt?.toDate?.() ?? new Date(),
      }));
      setNotifications(docs);
    }, err => console.error('Notifications snapshot error:', err));

    return () => unsub();
  }, [isReady, uid]);

  /* -------------------------------------------------------------- */
  /*  4. Calculer les badges                                         */
  /* -------------------------------------------------------------- */
  useEffect(() => {
    const next = {};
    WATCHED_ROUTES.forEach(r => {
      const total = totalSizes[r.path]     ?? 0;
      const seen  = lastSeen[r.collection] ?? 0;
      next[r.path] = Math.max(0, total - seen);
    });
    setBadge(next);
  }, [totalSizes, lastSeen]);

  /* -------------------------------------------------------------- */
  /*  5. Actions                                                     */
  /* -------------------------------------------------------------- */
  const markAsRead = useCallback(async (path, collectionName) => {
    if (!seenDocRef || !uid) return;
    try {
      const total = totalSizes[path] ?? 0;

      await updateDoc(seenDocRef, { [collectionName]: total });
      setLastSeen(prev => ({ ...prev, [collectionName]: total }));
      setBadge(prev => ({ ...prev, [path]: 0 }));

      const notifSnap = await getDocs(query(collection(db, 'notifications')));
      const batch = notifSnap.docs
        .filter(d => d.data().collection === collectionName && !(d.data().readBy ?? []).includes(uid))
        .map(d => updateDoc(d.ref, { readBy: [...(d.data().readBy ?? []), uid] }));
      await Promise.all(batch);
    } catch (err) {
      console.error('markAsRead error:', err);
    }
  }, [seenDocRef, uid, totalSizes]);

  const markAllRead = useCallback(async () => {
    if (!seenDocRef || !uid) return;
    try {
      const updates = {};
      WATCHED_ROUTES.forEach(r => {
        updates[r.collection] = totalSizes[r.path] ?? 0;
      });

      await updateDoc(seenDocRef, updates);
      setLastSeen(prev => ({ ...prev, ...updates }));

      const zeroBadge = {};
      WATCHED_ROUTES.forEach(r => { zeroBadge[r.path] = 0; });
      setBadge(zeroBadge);

      const notifSnap = await getDocs(collection(db, 'notifications'));
      const batch = notifSnap.docs
        .filter(d => !(d.data().readBy ?? []).includes(uid))
        .map(d => updateDoc(d.ref, { readBy: [...(d.data().readBy ?? []), uid] }));
      await Promise.all(batch);
    } catch (err) {
      console.error('markAllRead error:', err);
    }
  }, [seenDocRef, uid, totalSizes]);

  const publishNotification = useCallback(async ({ collectionName, label, count }) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        collection: collectionName,
        label,
        count,
        createdAt:  serverTimestamp(),
        readBy:     [],
        createdBy:  uid ?? 'system',
      });
    } catch (err) {
      console.error('publishNotification error:', err);
    }
  }, [uid]);

  /* -------------------------------------------------------------- */
  /*  Valeurs dérivées                                               */
  /* -------------------------------------------------------------- */
  const totalUnread      = Object.values(badge).reduce((a, b) => a + b, 0);
  const unreadNotifCount = notifications.filter(n => !n.isRead).length;

  return {
    badge,
    totalUnread,
    notifications,
    unreadNotifCount,
    isReady,
    soundEnabled,
    markAsRead,
    markAllRead,
    publishNotification,
    setSoundEnabled,
  };
}